/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useState, useMemo } from "react";
import { 
  Box, 
  Stack, 
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import DetailRow from "../components/DetailRow";
import AlarmTable from "../components/AlarmTable";
import DasPowerDetectorChart from "./DasPowerDetectorChart";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { 
  createPowerDetectorChartConfig, 
  buildPowerDetectorParams,
  PowerDetectorParams 
} from "./dasChartUtils";
import {
  formatPower,
  formatOnOff,
  formatGainMode,
  isValidPowerSignal,
  getSignalLedColor,
} from "../utils";

const RimDeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
}) => {
  const maxPower = parsedAttributes.maxPower || parsedAttributes.MaxPower;
  const ulGainMode = parsedAttributes.ulGainMode || parsedAttributes.UlGainMode;
  const ulGain = parsedAttributes.ulGain || parsedAttributes.UlGain;
  const rimALC = parsedAttributes.rimALC;
  const rimServiceState = parsedAttributes.rimServiceState;
  const ulPower = parsedAttributes.ulPower || parsedAttributes.UlPower;
  const dlPower = parsedAttributes.dlPower;
  const rimDetectedInPower = parsedAttributes.rimDetectedInPower;
  const rimServiceGroup1 = parsedAttributes.rimServiceGroup1;
  const rimServiceGroup2 = parsedAttributes.rimServiceGroup2;
  const rimServiceGroup3 = parsedAttributes.rimServiceGroup3;
  const rimAlarms = parsedAttributes.rimAlarms;
  const rimAlarmsMask = parsedAttributes.rimAlarmsMask;

  const hasBasicParams = maxPower || ulGainMode !== undefined || ulGain || rimALC !== undefined || 
                         rimServiceState !== undefined || dlPower;
  const hasChartParams = rimDetectedInPower !== undefined;
  const hasRfParams = hasBasicParams || hasChartParams;
  const hasServiceGroups = rimServiceGroup1 !== undefined || rimServiceGroup2 !== undefined || 
                           rimServiceGroup3 !== undefined;
  const hasAlarms = rimAlarms !== undefined || rimAlarmsMask !== undefined;

  // RemoteEquipment object_key for chart queries
  const remoteEquipmentObjectKey = useMemo(() => {
    return device.objectKey ||
      parsedAttributes.objectKey ||
      parsedAttributes.manageObject ||
      parsedAttributes.managedObject ||
      parsedAttributes.objectId ||
      'RIM-1';
  }, [
    JSON.stringify([
      device.objectKey,
      parsedAttributes.objectKey,
      parsedAttributes.manageObject,
      parsedAttributes.managedObject,
      parsedAttributes.objectId
    ])
  ]);

  const siteId = device.servicesDeviceObjectKey || device.serialNumber || '';

  // Chart configuration for Detected Input Power
  const detectedParams = useMemo<PowerDetectorParams | null>(() => {
    if (!rimDetectedInPower) return null;
    return {
      remoteEquipmentObjectKey,
      indicators: ['rimDetectedInPower'],
      bandNames: ['Detected Input Power'],
      bandNumbers: [1],
      yAxisMin: -50,
      yAxisMax: 50,
    };
  }, [remoteEquipmentObjectKey, rimDetectedInPower]);

  const detectedChartConfig = useMemo(() => 
    detectedParams ? createPowerDetectorChartConfig('dl', detectedParams) : null,
    [detectedParams]
  );

  const detectedChartAdditionalParams = useMemo(() => 
    detectedParams ? {
      ...buildPowerDetectorParams(siteId, remoteEquipmentObjectKey, ['rimDetectedInPower'])
    } : null,
    [detectedParams, siteId, remoteEquipmentObjectKey]
  );

  // State for fullscreen chart
  const [fullscreenChart, setFullscreenChart] = useState<'detected' | null>(null);
  
  const handleOpenFullscreen = (chartType: 'detected') => {
    setFullscreenChart(chartType);
  };
  
  const handleCloseFullscreen = () => {
    setFullscreenChart(null);
  };

  if (!hasRfParams && !hasServiceGroups && !hasAlarms) return null;

  const alarmDefinitions: AlarmDefinition[] = [
    { bit: 0, name: "DL Input Power Low" },
    { bit: 1, name: "DL Power Overload" },
    { bit: 2, name: "Service Off" },
    { bit: 3, name: "Output Power" },
    { bit: 4, name: "DL Out Power High" },
    { bit: 5, name: "Over Temperature" },
    { bit: 6, name: "Inconsistent Version" },
    { bit: 7, name: "Adjustment Fault" },
    { bit: 8, name: "HW Failure" },
    { bit: 9, name: "UL Synthesizer Unlock" },
    { bit: 10, name: "DL Synthesizer Unlock" },
    { bit: 11, name: "Reference Clock Unlock" },
    { bit: 12, name: "TDD Sync Loss" },
    { bit: 13, name: "AWS3 Band Mismatch" },
  ];

  const formatGain = (gain: any) => {
    const gainValue = parseFloat(gain);
    if (isNaN(gainValue)) return gain;
    return `${gainValue.toFixed(2)} dB`;
  };

  const isDisconnected = statusLabel === "Disconnected";

  // If alarmsOnly mode, skip the early return check for other data
  if (alarmsOnly) {
    if (!hasAlarms) return null;
    
    return (
      <>
        {hasAlarms && (
          <Card className="mb-2 overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-sm">Alarms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <AlarmTable
                alarms={rimAlarms}
                alarmsMask={rimAlarmsMask}
                alarmDefinitions={alarmDefinitions}
                isDisconnected={isDisconnected}
              />
            </CardContent>
          </Card>
        )}
      </>
    );
  }

  // Normal mode: show all sections including alarms
  if (!hasRfParams && !hasServiceGroups && !hasAlarms) return null;

  return (
    <>
      {hasBasicParams && (
        <Card className="mb-2">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">RF Parameters</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            {maxPower && <DetailRow label="Max Expected Power" value={formatPower(maxPower)} />}
            {ulGainMode !== undefined && <DetailRow label="UL Gain Mode" value={formatGainMode(ulGainMode)} />}
            {ulGain && <DetailRow label="Uplink Gain" value={formatGain(ulGain)} />}
            {rimALC !== undefined && <DetailRow label="Automatic Limit Control" value={formatOnOff(rimALC)} />}
            {rimServiceState !== undefined && <DetailRow label="Service State" value={formatOnOff(rimServiceState)} />}
            {dlPower && <DetailRow label="Downlink Power" value={formatPower(dlPower)} />}
          </CardContent>
        </Card>
      )}

      {hasChartParams && (
        <Card className="mb-2">
          <CardContent className="px-4 pb-4 pt-4">
            {rimDetectedInPower && detectedChartConfig && detectedChartAdditionalParams && (
              <>
                <div style={{ height: 300, width: '100%', marginBottom: 8 }}>
                  <DasPowerDetectorChart
                    config={{
                      ...detectedChartConfig,
                      title: 'Detected Input Power',
                      csvFilename: 'rim_detected_input_power'
                    }}
                    onFullscreenToggle={() => handleOpenFullscreen('detected')}
                    isFullscreen={false}
                    additionalParams={detectedChartAdditionalParams}
                  />
                </div>
                <div className="flex gap-2 flex-wrap mt-2">
                  <Card className="min-w-[120px] max-w-[180px]">
                    <CardContent className="p-4">
                      <p className="text-sm font-semibold text-foreground mb-1">Detected Input Power</p>
                      <div className="flex items-center gap-1.5">
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: getSignalLedColor(isValidPowerSignal(rimDetectedInPower)) }}
                        />
                        <span className="text-sm font-medium text-foreground">{formatPower(rimDetectedInPower)}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {hasServiceGroups && (
        <Card className="mb-2">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Service Groups</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            {rimServiceGroup1 !== undefined && <DetailRow label="Service Group 1" value={formatOnOff(rimServiceGroup1)} />}
            {rimServiceGroup2 !== undefined && <DetailRow label="Service Group 2" value={formatOnOff(rimServiceGroup2)} />}
            {rimServiceGroup3 !== undefined && <DetailRow label="Service Group 3" value={formatOnOff(rimServiceGroup3)} />}
          </CardContent>
        </Card>
      )}

      {/* Alarms Section */}
      {hasAlarms && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Alarms</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <AlarmTable
              alarms={rimAlarms}
              alarmsMask={rimAlarmsMask}
              alarmDefinitions={alarmDefinitions}
              isDisconnected={isDisconnected}
            />
          </CardContent>
        </Card>
      )}

      {/* Fullscreen Dialog for Detected Input Power Chart */}
      <Dialog
        open={fullscreenChart === 'detected'}
        onClose={handleCloseFullscreen}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "var(--card) !important",
            border: '1px solid var(--border)',
            borderRadius: '12px',
            minHeight: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '2px solid var(--border)',
          pb: 2,
          px: 4
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--foreground)' }}>
            Detected Input Power
          </Typography>
          <IconButton onClick={handleCloseFullscreen} size="small" sx={{ color: 'var(--foreground)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3, pb: 3 }}>
          {detectedChartConfig && detectedChartAdditionalParams && (
            <DasPowerDetectorChart
              config={{
                ...detectedChartConfig,
                title: 'Detected Input Power',
                csvFilename: 'rim_detected_input_power'
              }}
              onFullscreenToggle={handleCloseFullscreen}
              isFullscreen={true}
              additionalParams={detectedChartAdditionalParams}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RimDeviceDetails;
